import { type ReactNode } from 'react'

import { FavouriteButton } from '@/components/shared/listing/FavouriteButton'
import { type ListingCardPrice } from '@/components/srp/listing-card/types'
import { shouldShowPriceDrop, SRP_ATTRIBUTES } from '@/components/srp/search-list/domain'
import { isListingFromZipRecruiter } from '@/domain/listings/isListingFromZipRecruiter'
import { getListingProximityLabel } from '@/domain/srp/getListingProximityLabel'
import { getAttributesCanonicalValuesDictionary } from '@/features/attributes/utils/getAttributesCanonicalValuesDictionary'
import { RichCardContent } from '@/features/listing/components/rich-card/rich-card-content/RichCardContent'
import { RichCardFooter } from '@/features/listing/components/rich-card/rich-card-footer/RichCardFooter'
import { RichCardHeader } from '@/features/listing/components/rich-card/rich-card-header/RichCardHeader'
import { RichCardBadges } from '@/features/listing/components/rich-card/rich-card-media/RichCardBadges'
import { RichCardMedia } from '@/features/listing/components/rich-card/rich-card-media/RichCardMedia'
import { RichCardLinkProvider } from '@/features/listing/components/rich-card/RichCardLinkContext'
import {
  FavouriteButtonContainer,
  RichCardContainer,
  RichCardContentWrapper,
  RichCardMediaWrapper,
  RichCardWrapper,
} from '@/features/listing/components/rich-card/styled'
import { type GenericListing } from '@/features/listing/types/listings'

export type RichCardVariant = 'default' | 'compact'
export type RichCardLayout = 'vertical' | 'horizontal'
export type RichCardMediaType = 'image' | 'carousel'

export interface RichCardProps {
  /** Listing index */
  index: number
  /* Generic listing data from search or core listing */
  listing: GenericListing
  /** Default returns full rich card experience,
   * compact returns a 'Core' listing experience
   */
  variant?: RichCardVariant
  /* Layout of the card, can be horizontal (list) or vertical (card) */
  layout?: RichCardLayout
  /** Overrides the title of the listing
   *  For example, to highlight keywords */
  titleOverride?: string | ReactNode | null
  /** Media type of the card, can be image (default) or carousel */
  mediaType?: RichCardMediaType
  /** If true, the footer will be shown */
  showFooter?: boolean

  handleClick?: () => void
}

export const RichCard = ({
  index,
  listing,
  titleOverride,
  variant = 'default',
  layout = 'vertical',
  mediaType = 'image',
  showFooter = false,
  handleClick,
}: RichCardProps) => {
  const isHorizontalLayout = layout === 'horizontal'
  const isCompact = variant === 'compact'

  const { id, title, location, adSource, url, price } = listing
  const isZipRecruiterListing = isListingFromZipRecruiter(listing)

  // These fields are not always present in the listing object
  const flags = 'flags' in listing ? listing.flags : undefined
  const attributes = 'attributes' in listing ? listing.attributes : undefined
  const sortingDate = 'sortingDate' in listing ? listing.sortingDate : undefined
  const proximity =
    'distance' in listing.location ? getListingProximityLabel(listing.location) : undefined
  const posterInfo = 'posterInfo' in listing ? listing.posterInfo : undefined
  const rating = posterInfo && 'rating' in posterInfo ? posterInfo.rating : undefined

  const isHighlight = !!flags?.highlight || false

  const amount = (price && 'amount' in price && price.amount) || undefined
  const originalAmount = (price && 'originalAmount' in price && price.originalAmount) || undefined
  const surcharges = (price && 'surcharges' in price && price.surcharges) || undefined
  const hasPriceDropValue = shouldShowPriceDrop(!!flags?.priceDrop, originalAmount)

  const cardPrice: ListingCardPrice = {
    amount: amount,
    original: hasPriceDropValue ? originalAmount : undefined,
    type: price?.type,
    surcharges,
  }

  const attributesDictionary = getAttributesCanonicalValuesDictionary(attributes?.all)
  const logoSrc = attributesDictionary[SRP_ATTRIBUTES.LOGO]?.[0]

  return (
    <RichCardLinkProvider value={{ openInNewTab: isZipRecruiterListing }}>
      <RichCardContainer
        data-testid="rich-card"
        $isHighlight={isHighlight}
        $isHorizontalLayout={isHorizontalLayout}
        $isCompact={isCompact}
      >
        <RichCardWrapper $isHorizontalLayout={isHorizontalLayout}>
          <RichCardContentWrapper $isHorizontalLayout={isHorizontalLayout}>
            <RichCardHeader
              price={cardPrice}
              title={titleOverride || title}
              url={url}
              handleClick={handleClick}
            />

            {showFooter && !!logoSrc && (
              <RichCardFooter isHighlight={isHighlight} logoSrc={logoSrc} />
            )}

            <RichCardContent
              adSource={adSource}
              sortingDate={sortingDate}
              locationName={location.name}
              proximity={proximity}
              rating={rating}
            />
          </RichCardContentWrapper>
          <RichCardMediaWrapper $isHorizontalLayout={isHorizontalLayout}>
            <RichCardMedia
              listing={listing}
              index={index}
              mediaType={mediaType}
              titleOverride={titleOverride}
              isZipRecruiterListing={isZipRecruiterListing}
            />
            <RichCardBadges listing={listing} />
          </RichCardMediaWrapper>
          <FavouriteButtonContainer>
            {!isZipRecruiterListing ? (
              <FavouriteButton
                colorScheme="purple"
                listingId={id}
                variant="simple-with-background"
              />
            ) : null}
          </FavouriteButtonContainer>
        </RichCardWrapper>
      </RichCardContainer>
    </RichCardLinkProvider>
  )
}
